import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { CDMO_IMPORT_MESSAGES } from '../../constants/messages';
import {
  describeDuplicateShort,
  isImportableState,
  type CdmoImportRowState,
  type ValidatedCdmoImportRow,
} from '../../lib/cdmoImport/validateRow';
import RowDetail from './RowDetail';
import { ROW_STATE_LABELS } from './helpers';
import { StateBadge } from './shared';

export type PreviewFilter = 'all' | CdmoImportRowState;

interface PreviewStepProps {
  fileName: string;
  rows: ValidatedCdmoImportRow[];
  selected: ReadonlySet<number>;
  onSelectedChange: (next: Set<number>) => void;
  lookupError: string | null;
  onChooseAnotherFile: () => void;
}

const FILTERS: Array<{ key: PreviewFilter; label: string; activeTone: string }> = [
  { key: 'all', label: 'All rows', activeTone: 'border-electric-blue bg-electric-blue/10 text-selected-blue' },
  { key: 'ready', label: ROW_STATE_LABELS.ready, activeTone: 'border-emerald-500 bg-emerald-50 text-emerald-800' },
  { key: 'warning', label: ROW_STATE_LABELS.warning, activeTone: 'border-amber-500 bg-amber-50 text-amber-900' },
  { key: 'error', label: 'Errors', activeTone: 'border-red-500 bg-red-50 text-red-800' },
  { key: 'duplicate', label: 'Duplicates', activeTone: 'border-slate-500 bg-slate-100 text-slate-800' },
];

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The table's Issues cell: the row's first problem in words, so the fix is visible without opening it. */
const IssuesCell: React.FC<{ row: ValidatedCdmoImportRow }> = ({ row }) => {
  const duplicate = describeDuplicateShort(row);
  const problems = [...(duplicate ? [duplicate] : []), ...row.errors];
  if (problems.length > 0) {
    return (
      <div className="min-w-0">
        <p className={`text-xs line-clamp-2 break-words ${duplicate ? 'text-slate-700' : 'text-red-700'}`} title={problems[0]}>
          {problems[0]}
        </p>
        {problems.length > 1 && <p className="mt-0.5 text-xs text-slate-gray">+{problems.length - 1} more</p>}
      </div>
    );
  }
  if (row.warnings.length > 0) return <span className="text-xs text-amber-700">{plural(row.warnings.length, 'warning')}</span>;
  return <span className="text-xs text-slate-gray">—</span>;
};

const PreviewStep: React.FC<PreviewStepProps> = ({
  fileName,
  rows,
  selected,
  onSelectedChange,
  lookupError,
  onChooseAnotherFile,
}) => {
  const [filter, setFilter] = useState<PreviewFilter>('all');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const counts = useMemo(() => {
    const byState: Record<PreviewFilter, number> = { all: rows.length, ready: 0, warning: 0, error: 0, duplicate: 0 };
    for (const row of rows) byState[row.state] += 1;
    return byState;
  }, [rows]);

  const visible = useMemo(() => (filter === 'all' ? rows : rows.filter((row) => row.state === filter)), [rows, filter]);
  const visibleImportable = visible.filter((row) => isImportableState(row.state));
  const allVisibleSelected = visibleImportable.length > 0 && visibleImportable.every((row) => selected.has(row.rowNumber));
  const someVisibleSelected = visibleImportable.some((row) => selected.has(row.rowNumber)) && !allVisibleSelected;
  const warningRows = useMemo(() => rows.filter((row) => row.state === 'warning'), [rows]);
  // Follows the row ticks: on when no row with warnings is ticked, so it never disagrees with them.
  const warningsLeftOut = warningRows.length > 0 && warningRows.every((row) => !selected.has(row.rowNumber));

  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);

  const toggleRow = (rowNumber: number) => {
    const next = new Set(selected);
    if (next.has(rowNumber)) next.delete(rowNumber);
    else next.add(rowNumber);
    onSelectedChange(next);
  };

  const toggleAllVisible = () => {
    const next = new Set(selected);
    for (const row of visibleImportable) {
      if (allVisibleSelected) next.delete(row.rowNumber);
      else next.add(row.rowNumber);
    }
    onSelectedChange(next);
  };

  const toggleWarningRows = () => {
    const next = new Set(selected);
    for (const row of warningRows) {
      if (warningsLeftOut) next.add(row.rowNumber);
      else next.delete(row.rowNumber);
    }
    onSelectedChange(next);
  };

  const toggleExpanded = (rowNumber: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FileSpreadsheet className="w-5 h-5 text-electric-blue shrink-0" aria-hidden />
          <p className="text-sm font-semibold text-charcoal truncate" title={fileName}>{fileName}</p>
          <span className="text-sm text-slate-gray whitespace-nowrap">· {plural(rows.length, 'row')}</span>
        </div>
        <button
          type="button"
          onClick={onChooseAnotherFile}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-selected-blue hover:bg-electric-blue/10 transition-colors"
        >
          <RefreshCw className="w-4 h-4" aria-hidden />
          Choose a different file
        </button>
      </div>

      {lookupError && (
        <div role="status" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
          <span>{CDMO_IMPORT_MESSAGES.DUPLICATE_LOOKUP_FAILED(lookupError)}</span>
        </div>
      )}

      <div role="group" aria-label="Filter rows by status" className="flex flex-wrap gap-2">
        {FILTERS.map(({ key, label, activeTone }) => {
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => setFilter(key)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors ${
                active ? `${activeTone} font-medium` : 'border-slate-gray/20 bg-white text-slate-gray hover:bg-light-gray'
              }`}
            >
              {label}
              <span className="text-xs font-semibold tabular-nums">{counts[key]}</span>
            </button>
          );
        })}
      </div>

      {warningRows.length > 0 && (
        <label className="inline-flex items-center gap-2 text-sm text-charcoal cursor-pointer">
          <input
            type="checkbox"
            data-testid="cdmo-import-exclude-warnings"
            className="h-4 w-4 rounded border-slate-gray/40 text-electric-blue focus:ring-electric-blue"
            checked={warningsLeftOut}
            onChange={toggleWarningRows}
          />
          {CDMO_IMPORT_MESSAGES.EXCLUDE_WARNINGS(warningRows.length)}
        </label>
      )}

      <div className="rounded-xl border border-slate-gray/15 overflow-hidden">
        {/* `relative` makes this scroller the containing block of the header's sr-only label, which is
            absolutely positioned; otherwise it escapes the scroll box and widens the whole page. */}
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-light-gray text-left text-xs font-semibold uppercase tracking-wide text-slate-gray">
              <tr>
                <th scope="col" className="w-10 px-3 py-3">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    aria-label="Select all importable rows shown"
                    className="h-4 w-4 rounded border-slate-gray/40 text-electric-blue focus:ring-electric-blue disabled:opacity-40"
                    checked={allVisibleSelected}
                    disabled={visibleImportable.length === 0}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th scope="col" className="w-12 px-2 py-3">Row</th>
                <th scope="col" className="px-2 py-3">Company</th>
                <th scope="col" className="px-2 py-3">Website</th>
                <th scope="col" className="px-2 py-3">Modalities</th>
                <th scope="col" className="px-2 py-3">Status</th>
                <th scope="col" className="px-2 py-3 w-[30%]">Issues</th>
                <th scope="col" className="w-10 px-2 py-3"><span className="sr-only">Details</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-gray/10 bg-white">
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-gray">
                    No rows with this status.
                  </td>
                </tr>
              )}
              {visible.map((row) => {
                const importable = isImportableState(row.state);
                const isOpen = expanded.has(row.rowNumber);
                const company = row.companyName || row.raw.company_name?.trim() || 'Unnamed row';
                return (
                  <React.Fragment key={row.rowNumber}>
                    <tr
                      id={`cdmo-import-row-${row.rowNumber}`}
                      className={`align-top cursor-pointer transition-colors ${isOpen ? 'bg-electric-blue/5' : 'hover:bg-light-gray/60'}`}
                      onClick={() => toggleExpanded(row.rowNumber)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Import row ${row.rowNumber}, ${company}`}
                          className="h-4 w-4 rounded border-slate-gray/40 text-electric-blue focus:ring-electric-blue disabled:opacity-30"
                          checked={importable && selected.has(row.rowNumber)}
                          disabled={!importable}
                          onChange={() => toggleRow(row.rowNumber)}
                        />
                      </td>
                      <td className="px-2 py-3 text-slate-gray tabular-nums" title={`Spreadsheet row ${row.rowNumber + 1}`}>
                        {row.rowNumber}
                      </td>
                      <td className="px-2 py-3 font-medium text-charcoal max-w-[14rem] truncate" title={company}>{company}</td>
                      <td className="px-2 py-3 text-slate-gray max-w-[11rem] truncate" title={row.website}>
                        {row.normalizedDomain ?? (row.website || '—')}
                      </td>
                      <td className="px-2 py-3 text-slate-gray max-w-[11rem] truncate" title={row.profile.primary_capabilities.join(', ')}>
                        {row.profile.primary_capabilities.join(', ') || '—'}
                      </td>
                      <td className="px-2 py-3"><StateBadge state={row.state} /></td>
                      <td className="px-2 py-3"><IssuesCell row={row} /></td>
                      <td className="px-2 py-3">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? 'Hide' : 'Show'} details for row ${row.rowNumber}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(row.rowNumber);
                          }}
                          className="rounded-md p-1 text-slate-gray hover:bg-light-gray hover:text-charcoal"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-light-gray/40">
                        <td colSpan={8} className="px-4 py-4">
                          <RowDetail row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PreviewStep;
